import { cloneDeep, merge } from 'lodash'

import { AdvancedValue } from '@SonarCX/pages/Customers/FieldFilters/FieldPills'

import {
  CustomFilter,
  CREATED_MAP,
  DUE_MAP,
  DUE_RANGE,
  LEADS_FOR_ME,
  LEADS_FOR_USER,
  MY_PRIVATE_TASKS,
  MY_WATCHED_TASKS,
  TASKS_FOR_A_CLIENT,
  TASKS_FOR_USER,
  TASKS_FOR_WATCHER,
  TASKS_I_OWN,
  TASKS_OTHER_OWNER,
  TASK_ASSIGNED_BY_ME,
  TASK_ASSIGNED_TO_ME,
  VIEW_CLOSED,
  VIEW_OPEN
} from './parse'
import { INITIAL_VALUE } from '@SonarCX/providers/TaskFiltersProvider'

type StateType = typeof INITIAL_VALUE & Record<string, any>

const dissectLeadValue = (filter, userID: string) => {
  if (!filter.primary_assignee_id) {
    return {
      leadValue: 'null',
      leadID: 'null'
    }
  }

  if (filter.primary_assignee_id === userID) {
    return {
      leadValue: LEADS_FOR_ME,
      leadID: filter.primary_assignee_id
    }
  }

  return {
    leadValue: LEADS_FOR_USER,
    leadID: filter.primary_assignee_id
  }
}

const dissectOwnerValue = (filter, userID: string) => {
  if (!filter.owner_id) {
    return {
      ownerValue: 'null',
      ownerID: 'null'
    }
  }

  if (filter.owner_id === userID) {
    return {
      ownerValue: TASKS_I_OWN,
      ownerID: filter.owner_id
    }
  }

  return {
    ownerValue: TASKS_OTHER_OWNER,
    ownerID: filter.owner_id
  }
}

const dissectTaskValue = (filter, userID: string) => {
  if (filter.owner_id === userID && filter.assigned_to_other === userID) {
    return {
      taskValue: TASK_ASSIGNED_BY_ME,
      taskID: 'null'
    }
  }

  if (filter.owner_id === userID && filter.assigned_to_only === userID) {
    return {
      taskValue: MY_PRIVATE_TASKS,
      taskID: 'null'
    }
  }

  if (
    Array.isArray(filter.assigned_to) &&
    filter.assigned_to.includes(userID)
  ) {
    return {
      taskValue: TASK_ASSIGNED_TO_ME,
      taskID: 'null'
    }
  }

  if (
    Array.isArray(filter.assigned_to) &&
    !filter.assigned_to.includes(userID)
  ) {
    return {
      taskValue: TASKS_FOR_USER,
      taskID: filter.assigned_to[0]
    }
  }

  return {
    taskValue: 'null',
    taskID: 'null'
  }
}

const dissectWatcherValue = (filter, userID: string) => {
  if (!Array.isArray(filter.for_user_ids) || filter.for_user_ids.length === 0) {
    return {
      watcherValue: 'null',
      watcherID: 'null'
    }
  }

  if (filter.for_user_ids.includes(userID)) {
    return {
      watcherValue: MY_WATCHED_TASKS,
      watcherID: 'null'
    }
  }

  return {
    watcherValue: TASKS_FOR_WATCHER,
    watcherID: filter.for_user_ids[0]
  }
}

const dissectClientValue = filter => {
  if (!filter.concerning_user_id) {
    return {
      clientValue: 'null',
      clientID: 'null'
    }
  }

  return {
    clientValue: TASKS_FOR_A_CLIENT,
    clientID: filter.concerning_user_id
  }
}

const dissectIDValue = (filter, type: string) => {
  const id = `${type}_ids`
  const key = `${type}Value`
  return {
    [key]: filter[id] || 'null'
  }
}

const dissectDateValue = (
  filter,
  map: Record<string, number>,
  type: string
) => {
  const id = `${type}_within`

  let val = 'null'
  const filterVal = +filter[id]

  Object.entries(map).forEach(([key, value]) => {
    if (filterVal === value) {
      val = key
    }
  })

  return {
    [`${type}Value`]: val
  }
}

const dissectCompleteValue = filter => {
  if (filter.closed_only) {
    return {
      completeValue: VIEW_CLOSED
    }
  }

  return {
    completeValue: VIEW_OPEN
  }
}

const dissectDatetimeValue = (filter, id: string) => {
  const key = id === 'due_start' ? 'dueStartValue' : 'dueEndValue'

  if (filter[id]) {
    return {
      dueValue: DUE_RANGE,
      [key]: new Date(filter[id]).toISOString()
    }
  }

  return {
    [key]: 'null'
  }
}

const dissectState = (filter, userID: string) => {
  let state: StateType = cloneDeep(INITIAL_VALUE)

  state = merge(state, dissectLeadValue(filter, userID))
  state = merge(state, dissectOwnerValue(filter, userID))
  state = merge(state, dissectTaskValue(filter, userID))
  state = merge(state, dissectWatcherValue(filter, userID))
  state = merge(state, dissectClientValue(filter))

  state = merge(state, dissectIDValue(filter, 'category'))
  state = merge(state, dissectIDValue(filter, 'status'))
  state = merge(state, dissectIDValue(filter, 'type'))
  state = merge(state, dissectIDValue(filter, 'priority'))
  state = merge(state, dissectIDValue(filter, 'team'))

  state = merge(state, dissectDateValue(filter, DUE_MAP, 'due'))
  state = merge(state, dissectDateValue(filter, CREATED_MAP, 'created'))

  state = merge(state, dissectCompleteValue(filter))

  state = merge(state, dissectDatetimeValue(filter, 'due_start'))
  state = merge(state, dissectDatetimeValue(filter, 'due_end'))

  return state
}

const dissectFields = filter => {
  const filters: string[] | CustomFilter[] = filter.filter_custom || []

  if (filters.length === 0) {
    return []
  }

  const val = filters[0]

  if (typeof val === 'string') {
    return []
  }

  const values: AdvancedValue[] = []

  ;(filters as CustomFilter[]).forEach(filter => {
    const { field_id, logic, value } = filter
    values.push({
      field_id,
      value,
      logic
    })
  })

  return values
}

export const dissectFilter = (filter, userID: string) => {
  const state = dissectState(filter, userID)

  const includeSubtasks: boolean = filter.include_subtasks === 'true'

  const fieldValues = dissectFields(filter)

  return {
    state,
    includeSubtasks,
    fieldValues
  }
}
